/**
 * Login gate inside the acute-case drawer. Two shared role logins, not
 * per-person accounts: one surgery terminal, one reception terminal.
 */

import { Stethoscope, UserRound } from 'lucide-react'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import type { DeskRole } from '@/lib/desk-session-storage'
import { cn } from '@/utils/misc'

const ROLES: {
	value: DeskRole
	label: string
	hint: string
	icon: typeof Stethoscope
}[] = [
	{
		value: 'ordinace',
		label: 'Ordinace',
		hint: 'Psaní poznámek k akutním případům',
		icon: Stethoscope,
	},
	{
		value: 'recepce',
		label: 'Recepce',
		hint: 'Čtení poznámek a uzavírání případů',
		icon: UserRound,
	},
]

interface DeskLoginFormProps {
	onSubmit: (role: DeskRole, password: string) => void
	isPending: boolean
	error: string | null
}

export function DeskLoginForm({
	onSubmit,
	isPending,
	error,
}: DeskLoginFormProps) {
	const [role, setRole] = useState<DeskRole>('ordinace')
	const [password, setPassword] = useState('')

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!password || isPending) return
		onSubmit(role, password)
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-5">
			<p className="text-sm text-[#6b5c4c]">
				Vyberte pracoviště a zadejte heslo.
			</p>

			<div className="grid grid-cols-2 gap-3">
				{ROLES.map(({ value, label, hint, icon: Icon }) => (
					<button
						key={value}
						type="button"
						onClick={() => setRole(value)}
						aria-pressed={role === value}
						className={cn(
							'flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
							role === value
								? 'border-[#d7ad6c] bg-[#f5ebe0]'
								: 'border-[#ddd5c8] bg-white hover:border-[#d7ad6c] hover:bg-[#faf8f5]',
						)}
					>
						<span className="flex items-center gap-2 text-sm font-medium text-[#3d3428]">
							<Icon
								className={cn(
									'h-4 w-4',
									role === value ? 'text-[#d7ad6c]' : 'text-[#a09689]',
								)}
							/>
							{label}
						</span>
						<span className="text-[11px] leading-tight text-[#6b5c4c]">
							{hint}
						</span>
					</button>
				))}
			</div>

			<div className="space-y-2">
				<label
					htmlFor="desk-password"
					className="text-sm font-medium text-[#3d3428]"
				>
					Heslo
				</label>
				<Input
					id="desk-password"
					type="password"
					autoComplete="current-password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					disabled={isPending}
					// Explicit colours rather than the `bg-background` token: the app
					// boots with data-theme="dark" (contexts/theme-context.tsx
					// defaults to 'dark'), and DaisyUI 5's dark themes set
					// `color-scheme: dark`, which renders this control dark against
					// the drawer's hardcoded light palette.
					className="border-[#ddd5c8] bg-white text-[#3d3428] focus:border-[#d7ad6c] focus-visible:ring-[#d7ad6c]/20"
				/>
			</div>

			{error && (
				<p role="alert" className="text-sm text-red-700">
					{error}
				</p>
			)}

			<button
				type="submit"
				disabled={!password || isPending}
				className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-[#d7ad6c] px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#c49b5a] disabled:cursor-not-allowed disabled:opacity-50"
			>
				{isPending ? 'Přihlašuji…' : 'Přihlásit'}
			</button>
		</form>
	)
}
